import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { formApi } from '../../services';
import { PaymentDetails } from '../../components/shared/PaymentDetails';
import {
  LeadVerificationDetails,
  isKycValue,
  isEsignValue,
} from '../../components/shared/LeadVerificationDetails';
import { DetailRow } from '../../components/shared/DetailRow';
import { formatBookingValue } from './calendar/bookingEvents';
import { ADMIN_COLORS } from '../../theme';
import '../../styles/adminSurface.css';
import './ResponseDetail.css';

interface WorkflowOutcome {
  status: 'created' | 'skipped' | 'failed';
  workflowId?: string;
  reason?: string;
  error?: string;
  at?: string;
}

interface ResponseData {
  id: string;
  _id?: string;
  formId: string;
  createdAt: string;
  data: Record<string, any>;
  /** Tenant the Payment / KYC / eSign lead lives in, when not the form's own. */
  leadTenantId?: string;
  workflow?: WorkflowOutcome;
}

const isPaymentValue = (
  value: any,
): value is { leadId?: string; paymentId?: string } =>
  value && typeof value === 'object' && value.leadId && value.paymentId;

interface SubField {
  label: string;
  value: any;
}


const isCompositeValue = (value: any): value is Record<string, SubField> =>
  value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.values(value).every(
    (entry: any) =>
      entry && typeof entry === 'object' && 'label' in entry && 'value' in entry,
  ) &&
  Object.keys(value).length > 0;


const WORKFLOW_TONE = {
  created: { fg: ADMIN_COLORS.success, bg: ADMIN_COLORS.successSoft, border: ADMIN_COLORS.successBorder },
  failed: { fg: ADMIN_COLORS.danger, bg: ADMIN_COLORS.dangerSoft, border: ADMIN_COLORS.dangerBorder },
  skipped: { fg: ADMIN_COLORS.text, bg: ADMIN_COLORS.headBg, border: ADMIN_COLORS.border },
};

const UUID_SUFFIX_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const formatFieldLabel = (key: string) => {
  const stripped = key.replace(UUID_SUFFIX_RE, '').trim() || key;
  const spaced = stripped.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const ResponseDetail: React.FC = () => {
  const { tenantId, responseId } = useParams<{
    tenantId?: string;
    responseId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo: string | undefined = (location.state as { backTo?: string } | null)?.backTo;
  const [response, setResponse] = useState<ResponseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const fetchResponse = async () => {
      try {
        setLoading(true);
        const result = await formApi.getResponse(responseId!);
        setResponse(result);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    if (responseId) {
      fetchResponse();
    }
  }, [responseId, tenantId]);

  if (loading) {
    return (
      <Box className="response-detail-loading">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!response) {
    return (
      <Box className="admin-page response-detail-page">
        <Typography color="error">Response not found</Typography>
      </Box>
    );
  }

  const fieldCount = Object.keys(response.data).length;
  // Leads and their KYC / eSign / payment records live in the tenant the form's
  // gateway setting pointed at when the response was submitted.
  const leadTenantId = response.leadTenantId || tenantId;
  const workflow = response.workflow;
  const workflowTone = workflow?.status === 'created' || workflow?.status === 'failed' ? workflow.status : 'skipped';

  const handleRetryWorkflow = async () => {
    if (!tenantId) return;
    setRetrying(true);
    try {
      const result = await formApi.retryWorkflow(tenantId, response.formId, response.id);
      setResponse({ ...response, workflow: result.workflow });
    } catch (error: any) {
      setResponse({
        ...response,
        workflow: {
          status: 'failed',
          error:
            (typeof error.response?.data === 'string' && error.response.data) ||
            error.message ||
            'Retry failed',
        },
      });
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Box className="admin-page response-detail-page">
      <Box className="response-detail-header">
        <IconButton
          className="admin-back-btn"
          onClick={() => navigate(backTo || `/tenant/${tenantId}/form/${response.formId}/responses`)}
        >
          <ArrowBack sx={{ fontSize: 18 }} />
        </IconButton>
        <Box>
          <Typography className="admin-page-title">Form Response</Typography>
          <Typography className="admin-page-subtitle">
            Submitted on{' '}
            {new Date(response.createdAt).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })}
          </Typography>
        </Box>
        <Typography className="response-detail-count">
          {fieldCount} field{fieldCount === 1 ? '' : 's'}
        </Typography>
      </Box>

      {workflow && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: WORKFLOW_TONE[workflowTone].border,
            bgcolor: WORKFLOW_TONE[workflowTone].bg,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: WORKFLOW_TONE[workflowTone].fg }}>
            Workflow:{' '}
            {workflow.status === 'created'
              ? 'created'
              : workflow.status === 'failed'
                ? 'failed'
                : 'skipped'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', flex: 1, minWidth: 200 }}>
            {workflow.status === 'created'
              ? workflow.workflowId
                ? `Workflow id ${workflow.workflowId}`
                : ''
              : workflow.error || workflow.reason || ''}
          </Typography>
          {workflow.status !== 'created' && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleRetryWorkflow}
              disabled={retrying}
              sx={{ textTransform: 'none' }}
            >
              {retrying ? 'Retrying…' : 'Retry'}
            </Button>
          )}
        </Box>
      )}

      <Box className="response-detail-card">
        <TableContainer className="response-detail-table-scroll">
          <Table className="response-detail-table">
            <TableHead>
              <TableRow className="admin-table-head-row">
                <TableCell className="response-detail-head-field">Field</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(response.data).map(([key, value], index) => {
                const isPayment = isPaymentValue(value);
                const isKyc = isKycValue(value);
                const isEsign = isEsignValue(value);
                const isComposite =
                  !isPayment && !isKyc && !isEsign && isCompositeValue(value);
                const displayValue =
                  formatBookingValue(key, value) ??
                  (!isPayment && !isKyc && !isEsign && !isComposite && typeof value === 'object' && value !== null
                    ? Array.isArray(value)
                      ? value.join(', ')
                      : JSON.stringify(value, null, 2)
                    : String(value));

                return (
                  <TableRow
                    key={key}
                    className={`response-detail-row${index % 2 === 1 ? ' response-detail-row--alt' : ''}`}
                  >
                    <TableCell className="response-detail-field-cell">
                      <Typography className="response-detail-field-name">
                        {formatFieldLabel(key)}
                      </Typography>
                    </TableCell>
                    <TableCell className="response-detail-value-cell">
                      {isPayment ? (
                        <PaymentDetails tenantId={leadTenantId} payment={value} />
                      ) : isKyc ? (
                        <LeadVerificationDetails tenantId={leadTenantId} type="kyc" leadId={value.leadId} />
                      ) : isEsign ? (
                        <LeadVerificationDetails
                          tenantId={leadTenantId}
                          type="esign"
                          leadId={value.leadId}
                          verificationId={value.verificationId}
                          docId={value.docId}
                        />
                      ) : isComposite ? (
                        <Box className="response-detail-composite">
                          {Object.values(value as Record<string, SubField>).map((sub, i) => (
                            <DetailRow key={i} label={`${sub.label}:`}>
                              {sub.value || <em className="response-detail-value-empty">—</em>}
                            </DetailRow>
                          ))}
                        </Box>
                      ) : (
                        <Typography className="response-detail-value">
                          {displayValue || <em className="response-detail-value-empty">No value provided</em>}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
